import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all categories
export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async ({ page = 1, per_page = 15, status, search } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    
    const res = await http.get(`/dashboard/categories?${params.toString()}`);
    return res;
  }
);

// Create category
export const createCategory = createAsyncThunk(
  "categories/createCategory",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/categories", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create category"
      );
    }
  }
);

// Fetch category by UUID
export const fetchCategoryById = createAsyncThunk(
  "categories/fetchCategoryById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/categories/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch category details"
      );
    }
  }
);

// Update category
export const updateCategory = createAsyncThunk(
  "categories/updateCategory",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.post(`/dashboard/categories/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update category"
      );
    }
  }
);

// Delete category
export const deleteCategory = createAsyncThunk(
  "categories/deleteCategory",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/categories/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete category"
      );
    }
  }
);

// Enable category
export const enableCategory = createAsyncThunk(
  "categories/enableCategory",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/categories/${uuid}/enable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to enable category"
      );
    }
  }
);

// Disable category
export const disableCategory = createAsyncThunk(
  "categories/disableCategory",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/categories/${uuid}/disable`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to disable category"
      );
    }
  }
);

// Toggle category status
export const toggleCategoryStatus = createAsyncThunk(
  "categories/toggleCategoryStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/categories/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle category status"
      );
    }
  }
);

// Update category icon
export const updateCategoryIcon = createAsyncThunk(
  "categories/updateCategoryIcon",
  async ({ uuid, icon }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("icon", icon);
      
      const res = await http.post(`/dashboard/categories/${uuid}/update-icon`, formData);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update category icon"
      );
    }
  }
);

// Remove category icon
export const removeCategoryIcon = createAsyncThunk(
  "categories/removeCategoryIcon",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/categories/${uuid}/remove-icon`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to remove category icon"
      );
    }
  }
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState: {
    categories: [],
    pagination: null,
    category: null,
    categoryCreateStatus: "idle",
    categoryUpdateStatus: "idle",
    categoryDeleteStatus: "idle",
    categoryFetchStatus: "idle",
    categoryStatusChangeStatus: "idle",
    categoryIconUpdateStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetCategoryStatuses: (state) => {
      state.categoryCreateStatus = "idle";
      state.categoryUpdateStatus = "idle";
      state.categoryDeleteStatus = "idle";
      state.categoryStatusChangeStatus = "idle";
      state.categoryIconUpdateStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create category
      .addCase(createCategory.pending, (state) => {
        state.categoryCreateStatus = "loading";
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categoryCreateStatus = "success";
        state.category = action.payload.data;
      })
      .addCase(createCategory.rejected, (state) => {
        state.categoryCreateStatus = "failed";
      })

      // Fetch category by ID
      .addCase(fetchCategoryById.pending, (state) => {
        state.categoryFetchStatus = "loading";
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.categoryFetchStatus = "success";
        state.category = action.payload.data;
      })
      .addCase(fetchCategoryById.rejected, (state) => {
        state.categoryFetchStatus = "failed";
        state.category = null;
      })

      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.categoryUpdateStatus = "loading";
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.categoryUpdateStatus = "success";
        const index = state.categories.findIndex(
          (cat) => cat.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.categories[index] = action.payload.data;
        }
        state.category = action.payload.data;
      })
      .addCase(updateCategory.rejected, (state) => {
        state.categoryUpdateStatus = "failed";
      })

      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.categoryDeleteStatus = "loading";
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categoryDeleteStatus = "success";
        state.categories = state.categories.filter(
          (cat) => cat.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteCategory.rejected, (state) => {
        state.categoryDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleCategoryStatus.pending, (state) => {
        state.categoryStatusChangeStatus = "loading";
      })
      .addCase(toggleCategoryStatus.fulfilled, (state, action) => {
        state.categoryStatusChangeStatus = "success";
        const index = state.categories.findIndex(
          (cat) => cat.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.categories[index] = action.payload.data;
        }
      })
      .addCase(toggleCategoryStatus.rejected, (state) => {
        state.categoryStatusChangeStatus = "failed";
      })

      // Update icon
      .addCase(updateCategoryIcon.pending, (state) => {
        state.categoryIconUpdateStatus = "loading";
      })
      .addCase(updateCategoryIcon.fulfilled, (state, action) => {
        state.categoryIconUpdateStatus = "success";
        const index = state.categories.findIndex(
          (cat) => cat.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.categories[index] = action.payload.data;
        }
        if (state.category?.uuid === action.meta.arg.uuid) {
          state.category = action.payload.data;
        }
      })
      .addCase(updateCategoryIcon.rejected, (state) => {
        state.categoryIconUpdateStatus = "failed";
      })

      // Remove icon
      .addCase(removeCategoryIcon.fulfilled, (state, action) => {
        const index = state.categories.findIndex(
          (cat) => cat.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.categories[index] = action.payload.data;
        }
        if (state.category?.uuid === action.meta.arg) {
          state.category = action.payload.data;
        }
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetCategoryStatuses } = categoriesSlice.actions;
export default categoriesSlice.reducer;
